import React, { useState, useEffect, useRef } from 'react';
import OpenSeadragon from 'openseadragon';
import * as Annotorious from '@recogito/annotorious-openseadragon';
import '@recogito/annotorious-openseadragon/dist/annotorious.min.css';

interface AnnotationToolsProps {
  viewer: OpenSeadragon.Viewer | null;
  imageId: string;
  onSave?: (annotations: any[]) => void;
  initialAnnotations?: any[];
}

type DrawingTool = 'rect' | 'polygon' | 'circle' | 'freehand' | 'arrow' | 'text';
type AnnotationColor = string;

interface CustomAnnotation {
  id: string;
  type: string;
  body: {
    type: string;
    value: string;
    purpose: string;
    creator?: string;
    created?: string;
    color?: string;
  }[];
  target: any;
}

export function AnnotationTools({ 
  viewer, 
  imageId,
  onSave,
  initialAnnotations = []
}: AnnotationToolsProps) {
  const [annotorious, setAnnotorious] = useState<any>(null);
  const [currentTool, setCurrentTool] = useState<DrawingTool>('rect');
  const [currentColor, setCurrentColor] = useState<AnnotationColor>('#ff0000');
  const [annotations, setAnnotations] = useState<CustomAnnotation[]>(initialAnnotations);
  const [selectedAnnotation, setSelectedAnnotation] = useState<CustomAnnotation | null>(null);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [annotationText, setAnnotationText] = useState('');
  const [arrowInstructions, setArrowInstructions] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [annotoriousError, setAnnotoriousError] = useState<string | null>(null);
  const [isAnnotoriousReady, setIsAnnotoriousReady] = useState(false);
  const [availableTools, setAvailableTools] = useState<string[]>([]);

  // Predefined colors for different findings
  const annotationColors = [
    { name: 'Κόκκινο', value: '#ff0000', purpose: 'Παθολογική περιοχή' },
    { name: 'Πράσινο', value: '#00ff00', purpose: 'Φυσιολογική περιοχή' },
    { name: 'Μπλε', value: '#0000ff', purpose: 'Περιοχή ενδιαφέροντος' },
    { name: 'Κίτρινο', value: '#ffff00', purpose: 'Ύποπτη περιοχή' },
    { name: 'Μωβ', value: '#ff00ff', purpose: 'Φλεγμονή' },
    { name: 'Κυανό', value: '#00ffff', purpose: 'Νέκρωση' },
    { name: 'Πορτοκαλί', value: '#ff8800', purpose: 'Ίνωση' },
    { name: 'Λευκό', value: '#ffffff', purpose: 'Άλλο' }
  ];

  // Initialize Annotorious
  useEffect(() => {
    if (!viewer) return;

    const initAnnotorious = async () => {
      try {
        // Reset error state
        setAnnotoriousError(null);
        setIsAnnotoriousReady(false);

        // Check if viewer is ready and image is loaded
        if (!viewer.isOpen()) {
          viewer.addOnceHandler('open', initAnnotorious);
          return;
        }

        // Small delay to ensure image is fully loaded
        await new Promise(resolve => setTimeout(resolve, 500));

        const anno = Annotorious.default(viewer, {
          locale: 'el',
          allowEmpty: false,
          widgets: [
            'COMMENT',
            { widget: 'TAG', vocabulary: ['Παθολογικό', 'Φυσιολογικό', 'Ύποπτο', 'Φλεγμονή', 'Νέκρωση'] }
          ],
          formatter: (annotation: any) => {
            // Apply custom color to annotation
            const colorBody = annotation.body?.find((b: any) => b.purpose === 'styling');
            if (colorBody) {
              return {
                style: {
                  stroke: colorBody.value,
                  strokeWidth: 2,
                  fill: `${colorBody.value}33` // 20% opacity
                }
              };
            }
            return {
              style: {
                stroke: currentColor,
                strokeWidth: 2,
                fill: `${currentColor}33`
              }
            };
          }
        });

        // Test if Annotorious initialized properly
        if (!anno) {
          throw new Error('Annotorious failed to initialize');
        }

        // Set drawing tool safely
        try {
          anno.setDrawingTool('rect'); // Start with basic rectangle tool
        } catch (toolError) {
          console.warn('Failed to set drawing tool:', toolError);
        }

        // Event handlers
        anno.on('createAnnotation', (annotation: any) => {
          const customAnnotation = {
            ...annotation,
            body: [
              ...(annotation.body || []),
              {
                type: 'TextualBody',
                purpose: 'styling',
                value: currentColor
              },
              {
                type: 'TextualBody',
                purpose: 'tagging',
                value: getCurrentColorPurpose()
              }
            ]
          };
          
          setAnnotations([...annotations, customAnnotation]);
          onSave?.([...annotations, customAnnotation]);
        });

        anno.on('updateAnnotation', (annotation: any, previous: any) => {
          const updatedAnnotations = annotations.map(a => 
            a.id === previous.id ? annotation : a
          );
          setAnnotations(updatedAnnotations);
          onSave?.(updatedAnnotations);
        });

        anno.on('deleteAnnotation', (annotation: any) => {
          const filteredAnnotations = annotations.filter(a => a.id !== annotation.id);
          setAnnotations(filteredAnnotations);
          onSave?.(filteredAnnotations);
        });

        anno.on('selectAnnotation', (annotation: any) => {
          setSelectedAnnotation(annotation);
        });

        anno.on('cancelSelected', () => {
          setSelectedAnnotation(null);
        });

        // Load initial annotations
        if (initialAnnotations.length > 0) {
          anno.setAnnotations(initialAnnotations);
        }

        setAnnotorious(anno);
        setIsAnnotoriousReady(true);
        console.log('Annotorious initialized successfully');
        
        // Discover available tools
        const toolsToTest = ['rect', 'polygon', 'circle', 'ellipse', 'freehand', 'line', 'point', 'RECT', 'POLYGON', 'CIRCLE', 'ELLIPSE', 'FREEHAND', 'LINE', 'POINT'];
        const workingTools: string[] = [];
        
        console.log('🔍 Testing available annotation tools...');
        toolsToTest.forEach(tool => {
          try {
            anno.setDrawingTool(tool);
            workingTools.push(tool);
            console.log(`✅ Tool '${tool}' works`);
          } catch (e) {
            console.log(`❌ Tool '${tool}' not available:`, e);
          }
        });
        
        console.log('📋 Summary of working tools:', workingTools);
        console.log('🎯 Total working tools:', workingTools.length);
        
        // Also check what methods are available on the Annotorious instance
        console.log('🔧 Available methods on Annotorious:', Object.getOwnPropertyNames(anno).filter(name => typeof anno[name] === 'function'));
        console.log('🔧 Annotorious prototype methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(anno)).filter(name => typeof anno[name] === 'function'));
        
        setAvailableTools(workingTools);
        console.log('Available annotation tools:', workingTools);
        
        // Set back to default tool
        try {
          anno.setDrawingTool('rect');
        } catch (e) {
          console.warn('Could not set default rect tool');
        }

      } catch (error) {
        console.error('Annotorious initialization failed:', error);
        setAnnotoriousError(
          'Τα εργαλεία σχολιασμού δεν είναι διαθέσιμα για αυτή την εικόνα. ' +
          'Δοκιμάστε με διαφορετική εικόνα ή χρησιμοποιήστε άλλα εργαλεία.'
        );
        setAnnotorious(null);
        setIsAnnotoriousReady(false);
      }
    };

    // Start initialization
    initAnnotorious();

    return () => {
      try {
        if (annotorious) {
          annotorious.destroy();
        }
      } catch (error) {
        console.warn('Error destroying Annotorious:', error);
      }
    };
  }, [viewer]);

  // Update drawing tool
  useEffect(() => {
    if (annotorious && isAnnotoriousReady && availableTools.length > 0) {
      try {
        // Map our UI tools to available Annotorious tools
        const getAvailableTool = (preferredTools: string[]): string => {
          return preferredTools.find(tool => availableTools.includes(tool)) || availableTools[0] || 'rect';
        };
        
        let toolToSet = 'rect'; // Safe default
        
        switch (currentTool) {
          case 'rect':
            toolToSet = getAvailableTool(['rect', 'RECT']);
            break;
          case 'polygon':
            toolToSet = getAvailableTool(['polygon', 'POLYGON']);
            break;
          case 'circle':
            toolToSet = getAvailableTool(['circle', 'ellipse', 'CIRCLE', 'ELLIPSE']);
            break;
          case 'freehand':
            toolToSet = getAvailableTool(['freehand', 'FREEHAND', 'polygon', 'POLYGON']);
            break;
          case 'arrow':
            toolToSet = getAvailableTool(['line', 'LINE', 'arrow', 'ARROW', 'polygon', 'POLYGON']);
            break;
          case 'text':
            toolToSet = getAvailableTool(['point', 'POINT', 'rect', 'RECT']);
            break;
        }
        
        console.log(`Setting annotation tool: ${currentTool} -> ${toolToSet} (from available: ${availableTools.join(', ')})`);
        annotorious.setDrawingTool(toolToSet);
        
        // Show special instructions for arrow tool
        if (currentTool === 'arrow') {
          setArrowInstructions(true);
          // Auto-hide instructions after 5 seconds
          setTimeout(() => setArrowInstructions(false), 5000);
        } else {
          setArrowInstructions(false);
        }
        
      } catch (error) {
        console.warn('Failed to set drawing tool:', error, 'Falling back to first available tool');
        try {
          annotorious.setDrawingTool(availableTools[0] || 'rect');
        } catch (fallbackError) {
          console.error('Even fallback tool failed:', fallbackError);
        }
      }
    }
  }, [currentTool, annotorious, isAnnotoriousReady, availableTools]);

  // Get current color purpose
  const getCurrentColorPurpose = () => {
    const colorInfo = annotationColors.find(c => c.value === currentColor);
    return colorInfo?.purpose || 'Σημείωση';
  };

  // Create arrow-shaped polygon from two points
  const createArrowPolygon = (startX: number, startY: number, endX: number, endY: number) => {
    const arrowLength = Math.sqrt((endX - startX) ** 2 + (endY - startY) ** 2);
    const arrowWidth = Math.max(arrowLength * 0.1, 10); // Arrow width proportional to length
    const headLength = Math.max(arrowLength * 0.2, 15); // Arrow head length
    
    const angle = Math.atan2(endY - startY, endX - startX);
    
    // Calculate arrow points
    const points = [];
    
    // Arrow body (rectangle)
    const bodyWidth = arrowWidth * 0.4;
    const perpAngle = angle + Math.PI / 2;
    
    // Body start points
    points.push([
      startX + Math.cos(perpAngle) * bodyWidth / 2,
      startY + Math.sin(perpAngle) * bodyWidth / 2
    ]);
    points.push([
      startX - Math.cos(perpAngle) * bodyWidth / 2,
      startY - Math.sin(perpAngle) * bodyWidth / 2
    ]);
    
    // Body end points (before arrow head)
    const headStartX = endX - Math.cos(angle) * headLength;
    const headStartY = endY - Math.sin(angle) * headLength;
    
    points.push([
      headStartX - Math.cos(perpAngle) * bodyWidth / 2,
      headStartY - Math.sin(perpAngle) * bodyWidth / 2
    ]);
    points.push([
      headStartX - Math.cos(perpAngle) * arrowWidth / 2,
      headStartY - Math.sin(perpAngle) * arrowWidth / 2
    ]);
    
    // Arrow head point
    points.push([endX, endY]);
    
    // Other side of arrow head
    points.push([
      headStartX + Math.cos(perpAngle) * arrowWidth / 2,
      headStartY + Math.sin(perpAngle) * arrowWidth / 2
    ]);
    points.push([
      headStartX + Math.cos(perpAngle) * bodyWidth / 2,
      headStartY + Math.sin(perpAngle) * bodyWidth / 2
    ]);
    
    return points;
  };

  // Toggle annotation visibility
  const toggleAnnotationsVisibility = () => {
    if (annotorious) {
      if (showAnnotations) {
        annotorious.setVisible(false);
      } else {
        annotorious.setVisible(true);
      }
      setShowAnnotations(!showAnnotations);
    }
  };

  // Clear all annotations
  const clearAllAnnotations = () => {
    if (confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε όλες τις σημειώσεις;')) {
      if (annotorious) {
        annotorious.clearAnnotations();
      }
      setAnnotations([]);
      onSave?.([]);
    }
  };

  // Export annotations as JSON
  const exportAnnotations = () => {
    const dataStr = JSON.stringify(annotations, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `annotations_${imageId}_${Date.now()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Import annotations from JSON
  const importAnnotations = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedAnnotations = JSON.parse(e.target?.result as string);
        if (annotorious) {
          annotorious.setAnnotations(importedAnnotations);
        }
        setAnnotations(importedAnnotations);
        onSave?.(importedAnnotations);
      } catch (error) {
        alert('Σφάλμα κατά την εισαγωγή σημειώσεων');
      }
    };
    reader.readAsText(file);
  };

  // Add text annotation at current position
  const addTextAnnotation = () => {
    if (!annotationText.trim()) return;
    
    // Create a point annotation with text
    const textAnnotation: CustomAnnotation = {
      id: `text_${Date.now()}`,
      type: 'Annotation',
      body: [
        {
          type: 'TextualBody',
          value: annotationText,
          purpose: 'commenting',
          creator: 'user',
          created: new Date().toISOString()
        },
        {
          type: 'TextualBody',
          purpose: 'styling',
          value: currentColor
        }
      ],
      target: {
        selector: {
          type: 'FragmentSelector',
          conformsTo: 'http://www.w3.org/TR/media-frags/',
          value: 'xywh=pixel:100,100,50,50' // Default position
        }
      }
    };
    
    if (annotorious) {
      annotorious.addAnnotation(textAnnotation);
    }
    setAnnotations([...annotations, textAnnotation]);
    setAnnotationText('');
  };

  return (
    <div className="absolute top-20 left-80 z-20">
      <div className="bg-black bg-opacity-80 backdrop-blur-sm rounded-lg shadow-lg border border-white/20 p-3 w-72">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white text-sm font-semibold">Εργαλεία Σχολιασμού</h3>
          <div className="flex items-center space-x-2">
            {availableTools.length > 0 && (
              <div className="text-xs">
                <span className="text-gray-400" title={`Διαθέσιμα εργαλεία: ${availableTools.join(', ')}`}>
                  {availableTools.length} εργαλεία
                </span>
                <div className="text-xs text-gray-500">
                  {availableTools.includes('rect') && '◻️'}
                  {availableTools.includes('polygon') && '🔺'}
                  {availableTools.some(t => ['circle', 'ellipse'].includes(t)) && '⭕'}
                  {availableTools.some(t => ['line', 'arrow'].includes(t)) && '➡️'}
                  {availableTools.some(t => ['freehand'].includes(t)) && '✏️'}
                </div>
              </div>
            )}
            <div className={`w-2 h-2 rounded-full ${
              isAnnotoriousReady ? 'bg-green-500' : annotoriousError ? 'bg-red-500' : 'bg-yellow-500'
            }`} title={
              isAnnotoriousReady ? 'Έτοιμο' : annotoriousError ? 'Σφάλμα' : 'Φόρτωση...'
            } />
          </div>
        </div>
        
        {/* Error message */}
        {annotoriousError && (
          <div className="mb-3 p-3 bg-red-900 bg-opacity-50 rounded-lg border border-red-500">
            <div className="flex items-start space-x-2">
              <span className="text-red-400 text-lg">⚠️</span>
              <div>
                <p className="text-red-300 text-xs font-medium mb-1">Σφάλμα Σχολιασμού</p>
                <p className="text-red-200 text-xs">{annotoriousError}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Loading state */}
        {!isAnnotoriousReady && !annotoriousError && (
          <div className="mb-3 p-3 bg-blue-900 bg-opacity-50 rounded-lg border border-blue-500">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-blue-200 text-xs">Φόρτωση εργαλείων σχολιασμού...</p>
            </div>
          </div>
        )}
        
        {/* Tool-specific instructions */}
        {currentTool === 'arrow' && (
          <div className="mb-3 p-3 bg-blue-900 bg-opacity-50 rounded-lg border border-blue-500">
            <div className="flex items-start space-x-2">
              <span className="text-blue-400 text-lg">➡️</span>
              <div>
                <p className="text-blue-300 text-xs font-medium mb-1">Σχεδίαση Βέλους</p>
                <div className="text-blue-200 text-xs space-y-1">
                  <p>• Κάντε κλικ για να τοποθετήσετε σημεία του πολυγώνου</p>
                  <p>• Συνιστώμενα σημεία: αρχή → μέση → άκρη → πλευρές</p>
                  <p>• <strong>Διπλό κλικ</strong> για ολοκλήρωση</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {currentTool === 'circle' && (
          <div className="mb-3 p-3 bg-yellow-900 bg-opacity-50 rounded-lg border border-yellow-500">
            <div className="flex items-start space-x-2">
              <span className="text-yellow-400 text-lg">⭕</span>
              <div>
                <p className="text-yellow-300 text-xs font-medium mb-1">Σχεδίαση Κύκλου</p>
                <div className="text-yellow-200 text-xs space-y-1">
                  <p>• Χρησιμοποιεί πολύγωνο για κυκλικό σχήμα</p>
                  <p>• Κάντε κλικ γύρω από την περιμετρο (6-8 σημεία)</p>
                  <p>• <strong>Διπλό κλικ</strong> για ολοκλήρωση</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {currentTool === 'freehand' && (
          <div className="mb-3 p-3 bg-purple-900 bg-opacity-50 rounded-lg border border-purple-500">
            <div className="flex items-start space-x-2">
              <span className="text-purple-400 text-lg">✏️</span>
              <div>
                <p className="text-purple-300 text-xs font-medium mb-1">Ελεύθερο Σχέδιο</p>
                <div className="text-purple-200 text-xs space-y-1">
                  <p>• Χρησιμοποιεί πολύγωνο για ελεύθερο σχήμα</p>
                  <p>• Κάντε κλικ ακολουθώντας το επιθυμητό περίγραμμα</p>
                  <p>• <strong>Διπλό κλικ</strong> για ολοκλήρωση</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Drawing tools */}
        <div className={`grid grid-cols-3 gap-1 mb-3 ${!isAnnotoriousReady ? 'opacity-50 pointer-events-none' : ''}`}>
          {/* Helper function to check if tool is available */}
          {(() => {
            const isToolAvailable = (tool: DrawingTool) => {
              switch (tool) {
                case 'rect': return availableTools.some(t => ['rect', 'RECT'].includes(t));
                case 'polygon': return availableTools.some(t => ['polygon', 'POLYGON'].includes(t));
                case 'circle': return availableTools.some(t => ['circle', 'ellipse', 'CIRCLE', 'ELLIPSE'].includes(t));
                case 'freehand': return availableTools.some(t => ['freehand', 'FREEHAND', 'polygon', 'POLYGON'].includes(t));
                case 'arrow': return availableTools.some(t => ['line', 'LINE', 'arrow', 'ARROW', 'polygon', 'POLYGON'].includes(t));
                case 'text': return availableTools.some(t => ['point', 'POINT', 'rect', 'RECT'].includes(t));
                default: return false;
              }
            };
            
            return null; // This is just to define the helper function
          })()}
          <button
            onClick={() => setCurrentTool('rect')}
            disabled={!isAnnotoriousReady}
            className={`px-2 py-2 text-xs font-medium rounded transition-colors relative ${
              currentTool === 'rect' 
                ? 'bg-blue-600 text-white' 
                : availableTools.some(t => ['rect', 'RECT'].includes(t))
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-red-800 hover:bg-red-700 text-red-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            title={`Ορθογώνιο ${!availableTools.some(t => ['rect', 'RECT'].includes(t)) ? '(μη διαθέσιμο)' : ''}`}
          >
            ◻️
            {!availableTools.some(t => ['rect', 'RECT'].includes(t)) && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            )}
          </button>
          
          <button
            onClick={() => setCurrentTool('circle')}
            disabled={!isAnnotoriousReady}
            className={`px-2 py-2 text-xs font-medium rounded transition-colors relative disabled:opacity-50 disabled:cursor-not-allowed ${
              currentTool === 'circle' 
                ? 'bg-blue-600 text-white' 
                : availableTools.some(t => ['circle', 'ellipse', 'CIRCLE', 'ELLIPSE'].includes(t))
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-red-800 hover:bg-red-700 text-red-200'
            }`}
            title={`Κύκλος ${!availableTools.some(t => ['circle', 'ellipse', 'CIRCLE', 'ELLIPSE'].includes(t)) ? '(χρησιμοποιεί πολύγωνο)' : ''}`}
          >
            ⭕
            {!availableTools.some(t => ['circle', 'ellipse', 'CIRCLE', 'ELLIPSE'].includes(t)) && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-500 rounded-full" title="Χρησιμοποιεί εναλλακτικό εργαλείο"></span>
            )}
          </button>
          
          <button
            onClick={() => setCurrentTool('polygon')}
            disabled={!isAnnotoriousReady}
            className={`px-2 py-2 text-xs font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              currentTool === 'polygon' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-700 hover:bg-gray-600 text-white'
            }`}
            title="Πολύγωνο"
          >
            🔺
          </button>
          
          <button
            onClick={() => setCurrentTool('freehand')}
            disabled={!isAnnotoriousReady}
            className={`px-2 py-2 text-xs font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              currentTool === 'freehand' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-700 hover:bg-gray-600 text-white'
            }`}
            title="Ελεύθερο σχέδιο"
          >
            ✏️
          </button>
          
          <button
            onClick={() => setCurrentTool('arrow')}
            disabled={!isAnnotoriousReady}
            className={`px-2 py-2 text-xs font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              currentTool === 'arrow' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-700 hover:bg-gray-600 text-white'
            }`}
            title="Βέλος - Κάντε κλικ για σχεδίαση με πολύγωνο (3-4 σημεία)"
          >
            ➡️
          </button>
          
          <button
            onClick={() => setCurrentTool('text')}
            disabled={!isAnnotoriousReady}
            className={`px-2 py-2 text-xs font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              currentTool === 'text' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-700 hover:bg-gray-600 text-white'
            }`}
            title="Κείμενο"
          >
            📝
          </button>
        </div>
        
        {/* Color selection */}
        <div className="mb-3">
          <p className="text-xs text-gray-300 mb-2">Χρώμα & Σκοπός:</p>
          <div className="grid grid-cols-4 gap-1">
            {annotationColors.map((color) => (
              <button
                key={color.value}
                onClick={() => setCurrentColor(color.value)}
                className={`w-full h-8 rounded border-2 transition-all ${
                  currentColor === color.value 
                    ? 'border-white scale-110' 
                    : 'border-gray-600 hover:border-gray-400'
                }`}
                style={{ backgroundColor: color.value }}
                title={color.purpose}
              />
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1 text-center">
            {getCurrentColorPurpose()}
          </p>
        </div>
        
        {/* Text annotation input */}
        {currentTool === 'text' && (
          <div className="mb-3">
            <div className="flex space-x-1">
              <input
                type="text"
                value={annotationText}
                onChange={(e) => setAnnotationText(e.target.value)}
                placeholder="Κείμενο σημείωσης..."
                className="flex-1 px-2 py-1 text-xs bg-gray-700 text-white rounded"
                onKeyPress={(e) => e.key === 'Enter' && addTextAnnotation()}
              />
              <button
                onClick={addTextAnnotation}
                className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded"
              >
                ➕
              </button>
            </div>
          </div>
        )}
        
        {/* Visibility toggle */}
        <div className="border-t border-white/20 pt-2 mb-3">
          <button
            onClick={toggleAnnotationsVisibility}
            className={`w-full px-3 py-2 text-xs font-medium rounded transition-colors ${
              showAnnotations 
                ? 'bg-green-700 hover:bg-green-600 text-white' 
                : 'bg-gray-700 hover:bg-gray-600 text-white'
            }`}
          >
            {showAnnotations ? '👁️ Απόκρυψη Σημειώσεων' : '👁️ Εμφάνιση Σημειώσεων'}
          </button>
        </div>
        
        {/* Annotations list */}
        {annotations.length > 0 && (
          <div className="border-t border-white/20 pt-2 mb-3">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-300">Σημειώσεις ({annotations.length})</span>
              <button
                onClick={clearAllAnnotations}
                className="text-xs text-red-400 hover:text-red-300"
              >
                Καθαρισμός
              </button>
            </div>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {annotations.map((anno, index) => {
                const textBody = anno.body?.find(b => b.purpose === 'commenting');
                const colorBody = anno.body?.find(b => b.purpose === 'styling');
                const tagBody = anno.body?.find(b => b.purpose === 'tagging');
                
                return (
                  <div 
                    key={anno.id} 
                    className={`flex items-center justify-between p-1 rounded text-xs ${
                      selectedAnnotation?.id === anno.id ? 'bg-blue-900 bg-opacity-50' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full border border-white"
                        style={{ backgroundColor: colorBody?.value || '#ff0000' }}
                      />
                      <span className="text-gray-300 truncate max-w-[150px]">
                        {textBody?.value || tagBody?.value || `Σημείωση ${index + 1}`}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        if (annotorious) {
                          annotorious.removeAnnotation(anno);
                        }
                      }}
                      className="text-red-400 hover:text-red-300"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Export/Import */}
        <div className="border-t border-white/20 pt-2">
          <div className="flex space-x-1">
            <button
              onClick={exportAnnotations}
              className="flex-1 px-2 py-1 text-xs bg-green-700 hover:bg-green-600 text-white rounded"
            >
              💾 Εξαγωγή
            </button>
            <label className="flex-1">
              <input
                type="file"
                accept=".json"
                onChange={importAnnotations}
                className="hidden"
              />
              <span className="block px-2 py-1 text-xs bg-blue-700 hover:bg-blue-600 text-white rounded text-center cursor-pointer">
                📁 Εισαγωγή
              </span>
            </label>
          </div>
        </div>
        
        {/* Instructions */}
        <div className="mt-3 p-2 bg-gray-900 bg-opacity-50 rounded">
          <p className="text-xs text-gray-400">
            💡 Συμβουλές:
          </p>
          <ul className="text-xs text-gray-500 mt-1 space-y-1">
            <li>• Κλικ & σύρσιμο για σχεδίαση</li>
            <li>• Διπλό κλικ για επεξεργασία</li>
            <li>• Delete για διαγραφή</li>
            <li>• Shift+κλικ για πολλαπλή επιλογή</li>
          </ul>
        </div>
      </div>
    </div>
  );
}